import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

// Import all translations
import fr from './locales/fr';
import en from './locales/en';
import es from './locales/es';
import de from './locales/de';
import it from './locales/it';
import pt from './locales/pt';

const translations = { fr, en, es, de, it, pt };

const I18nContext = createContext();

export const LANGUAGES = [
  { code: 'fr', name: 'Français', flag: '🇫🇷' },
  { code: 'en', name: 'English', flag: '🇬🇧' },
  { code: 'es', name: 'Español', flag: '🇪🇸' },
  { code: 'de', name: 'Deutsch', flag: '🇩🇪' },
  { code: 'it', name: 'Italiano', flag: '🇮🇹' },
  { code: 'pt', name: 'Português', flag: '🇵🇹' }
];

export const I18nProvider = ({ children }) => {
  const [language, setLanguageState] = useState(() => {
    // Get language from localStorage or default to French for this French platform
    const saved = localStorage.getItem('sylink_language');
    if (saved && translations[saved]) return saved;

    // Default to French - this is a French platform (sylink-pentest.fr)
    return 'fr';
  });

  // Update localStorage when language changes
  useEffect(() => {
    localStorage.setItem('sylink_language', language);
    document.documentElement.lang = language;
  }, [language]);

  const setLanguage = useCallback((lang) => {
    if (translations[lang]) {
      setLanguageState(lang);
    }
  }, []);

  // Translation function with nested key support (e.g., "nav.dashboard")
  // SAFETY: This function ALWAYS returns a string - never an object.
  // This prevents React error #130 (Objects are not valid as React child).
  const t = useCallback((key, params = {}) => {
    if (!key || typeof key !== 'string') return String(key || '');

    const keys = key.split('.');
    let value = translations[language];

    for (const k of keys) {
      if (value && typeof value === 'object' && k in value) {
        value = value[k];
      } else {
        // Fallback to French if key not found
        value = translations['fr'];
        for (const fk of keys) {
          if (value && typeof value === 'object' && fk in value) {
            value = value[fk];
          } else {
            return String(key);
          }
        }
        break;
      }
    }

    // CRITICAL SAFETY: Always return a string, never an object or array
    if (value === null || value === undefined) {
      return String(key);
    }
    if (typeof value !== 'string') {
      return String(key);
    }

    // Replace parameters like {name}
    return value.replace(/\{(\w+)\}/g, (match, param) => {
      return params[param] !== undefined ? String(params[param]) : match;
    });
  }, [language]);

  // Get current language info
  const currentLanguage = LANGUAGES.find(l => l.code === language) || LANGUAGES[0];

  const value = {
    language,
    setLanguage,
    t,
    currentLanguage,
    languages: LANGUAGES
  };

  return (
    <I18nContext.Provider value={value}>
      {children}
    </I18nContext.Provider>
  );
};

// Hook for using translations
export const useI18n = () => {
  const context = useContext(I18nContext);
  if (!context) {
    throw new Error('useI18n must be used within an I18nProvider');
  }
  return context;
};

// HOC for class components
export const withI18n = (Component) => {
  return function WrappedComponent(props) {
    const i18n = useI18n();
    return <Component {...props} i18n={i18n} />;
  };
};

export default I18nContext;
